import {
    Autocomplete,
    AutocompleteCloseReason,
    AutocompleteRenderGroupParams,
    Box,
    Button,
    FormControl,
    FormHelperText,
    Paper,
    Popper,
    PopperProps,
    SxProps,
    TextField,
    Typography,
} from '@mui/material';
import _ from 'lodash';
import React, {
    HTMLAttributes,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { AxleTheme } from '../../theme';
import {
    AllowedInputValue,
    AllowedInputValueItem,
    InputState,
    polymorphicIsEqual,
} from '../../utils/inputState';
import { Children, ReturnComponent, Style } from '../../utils/reactTypes';
import { If } from '../utils/conditionalComponents';
import { FieldWithAxleLabel, labelWithRequiredAsterisk } from './AxleLabel';
import { fuzzyFilterOptions } from './fuzzyFilterOptions';
import { shiftKeepsDropdownOpen } from './shiftKeepsDropdownOpen';

// Sentinel values for buttons/labels - we use these so there is no possibility of
// a collision with actual options in the list
const SHOW_MORE_SENTINEL = Symbol('SHOW_MORE_BUTTON');
const DEFAULT_OPTIONS_SENTINEL = Symbol('DEFAULT_OPTIONS');
const MORE_OPTIONS_SENTINEL = Symbol('MORE_OPTIONS_SENTINEL');
const SENTINELS = {
    [SHOW_MORE_SENTINEL]: true,
    [DEFAULT_OPTIONS_SENTINEL]: true,
    [MORE_OPTIONS_SENTINEL]: true,
};

const FitContentPopper = ({ sx = {}, placement = 'bottom-start', ...props }: PopperProps) => {
    return <Popper {...props} sx={{ minWidth: 'fit-content', ...sx }} placement={placement} />;
};

export interface AxleAutoCompleteFieldProps<
    InputValue extends AllowedInputValue<AllowedInputValueItem>,
    Option,
> {
    label?: string;
    inlineLabel?: boolean;
    enabled?: boolean;
    disabledHelperText?: string;
    hideHelperText?: boolean;
    inputState: InputState<InputValue>;
    options: Option[];
    moreOptions?: Option[];
    showMoreButtonText?: string;
    optionToValue: (option: Option) => InputValue;
    optionToLabel: (option: Option) => string;
    renderOption?: (option: Option) => ReturnComponent;
    // When provided, options this returns true for render disabled (not selectable).
    optionToDisabled?: (option: Option) => boolean;
    // When provided, a non-null result renders right-aligned in the option row.
    optionToTag?: (option: Option) => React.ReactNode;
    // Explanatory text pinned above the options while the dropdown is open.
    hint?: string | null;
    groupBy?: (option: Option) => string;
    renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
    handleNullValue?: () => void;
    disableClearable?: boolean;
    sortOptions?: boolean;
    specialAutocompleteOnlyToAddMode?: boolean;
    filterOptions?: (options: Option[]) => Option[];
    style?: Style;
    tooltipText?: string;
    noOptionsText?: string;
    placeholder?: string;
    description?: Children;
    disableResetWithoutInput?: boolean;
    // Controls whether the controlled value should be displayed even if it's not
    // available in the options list. This behavior is natively supported by
    // Autocomplete when providing a `value` that's missing in the options list,
    // but our default wrapper behavior is to pass in `null` as the controlled
    // value in that case instead.
    //
    // Should only be set to `true` when the input value and options are of the
    // same type, otherwise, the value will fail to render.
    displayMissingOptionValue?: boolean;
    optionStyle?: SxProps<typeof AxleTheme>;
    _autocompleteRootInputStyles?: Style;
    expandMenuToFitContent?: boolean;
}

// Like <AxleSelectField/>, but lets the user type to filter the options.
// If you only have 3–4 fixed options, use <AxleSelectField/> instead.
//  Otherwise, for many options, use this.
export const AxleAutocompleteField = <
    Option,
    InputValue extends AllowedInputValue<AllowedInputValueItem>,
>({
    label = '',
    inlineLabel = false,
    enabled = true,
    tooltipText = '',
    disabledHelperText = '',
    hideHelperText = false,
    inputState: { value, required, setInputState, showError, helperText, setIsTouched, reset },
    options,
    moreOptions = [],
    showMoreButtonText = 'Show More',
    optionToValue,
    optionToLabel,
    renderOption,
    optionToDisabled,
    optionToTag,
    hint,
    // Use a custom popper so the menu items can be larger than the select field.
    expandMenuToFitContent = false,
    // Whether to show the “×” button to reset the input to its default value.
    disableClearable = false,
    handleNullValue,
    // Whether to sort the options.
    sortOptions = true,
    // Special flag used by <AxleMultiSelectWithChipsArray /> and
    //  other components that only use this field to add new items
    //  to a list, but don’t need this field to display a selected option.
    specialAutocompleteOnlyToAddMode = false,
    // Optional function to filter options down with a function.
    filterOptions,
    // Pass-through parameters.
    style,
    groupBy,
    renderGroup,
    noOptionsText,
    description,
    placeholder = 'Type to filter...',
    disableResetWithoutInput = false,
    displayMissingOptionValue = false,
    optionStyle,
    _autocompleteRootInputStyles: _extraAutocompleteStyles,
}: AxleAutoCompleteFieldProps<InputValue, Option>): ReturnComponent => {
    // Toggle to show more options when "Show More" is clicked
    const [showMore, setShowMore] = useState(false);

    const isSentinel = useCallback(
        (option: Option | null): boolean => Boolean(SENTINELS[option as keyof typeof SENTINELS]),
        [],
    );

    // Combine options based on whether "Show More" has been clicked
    const currentOptions = useMemo(() => {
        if (sortOptions) {
            const sortedInitialOptions = _.sortBy(options, optionToLabel);
            const sortedMoreOptions = _.sortBy(moreOptions, optionToLabel);
            return showMore
                ? [...sortedInitialOptions, ...sortedMoreOptions]
                : sortedInitialOptions;
        }
        return showMore ? [...options, ...moreOptions] : options;
    }, [moreOptions, optionToLabel, options, showMore, sortOptions]);

    const optionsProp = useMemo(() => {
        if (moreOptions.length === 0) {
            return currentOptions;
        }

        if (!showMore) {
            return currentOptions.concat([SHOW_MORE_SENTINEL as unknown as Option]);
        }
        return [
            DEFAULT_OPTIONS_SENTINEL,
            ...currentOptions.slice(0, options.length),
            MORE_OPTIONS_SENTINEL,
            ...currentOptions.slice(options.length),
        ];
    }, [currentOptions, moreOptions.length, options.length, showMore]);

    // If the options change, then mark the input as untouched, and reset the value.
    // We compare a stable scalar signature of the option *values* rather than deep-
    //  comparing the option objects. Deep object comparison (polymorphicIsEqual) is
    //  key-count sensitive and can return false for content-equal data once the option
    //  objects' shape drifts between renders (e.g. Apollo `__typename`/nested-object
    //  identity), which makes this effect setState on every render → infinite render loop
    //  that freezes the field. `optionToValue` returns a stable scalar (e.g. an id), so a
    //  signature built from it only changes when the options genuinely change. (GTM-307)
    const optionsSignature = useMemo(
        () => currentOptions.map((option) => JSON.stringify(optionToValue(option))).join('|'),
        [currentOptions, optionToValue],
    );
    const [lastOptionsSignature, setLastOptionsSignature] = useState(optionsSignature);
    useEffect(() => {
        if (optionsSignature === lastOptionsSignature) {
            return;
        }
        setLastOptionsSignature(optionsSignature);

        setIsTouched(false);
        if (!specialAutocompleteOnlyToAddMode) {
            // If there’s only one option, just select that automatically.
            if (currentOptions.length === 1) {
                setInputState(optionToValue(currentOptions[0]));
            } else if (!disableResetWithoutInput) {
                // Otherwise just reset to the original value.
                // Nick note: it's unclear to me why we like this default behavior of resetting
                //  the input to the reset value on first render. I added this boolean for when our
                //  initial value differs from the reset value and we actually want to start with
                //  the initial value + not update it if options change.
                reset();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionsSignature]);

    const [autocompleteTypedText, setAutocompleteTypedText] = useState('');

    // The dropdown is controlled so a pick made with shift held can leave it
    // open. Add mode is the multiselect's input, where picking repeatedly is the
    // whole point; a single-select field always closes on a pick.
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const openDropdown = useCallback(() => setIsDropdownOpen(true), []);
    const closeDropdown = useCallback(
        (event: SyntheticEvent, reason: AutocompleteCloseReason) => {
            if (specialAutocompleteOnlyToAddMode && shiftKeepsDropdownOpen(event, reason)) {
                return;
            }
            setIsDropdownOpen(false);
        },
        [specialAutocompleteOnlyToAddMode],
    );
    const matchingOptionValue = useMemo(
        () =>
            currentOptions.find((option) => polymorphicIsEqual(optionToValue(option), value)) ??
            null,
        [currentOptions, optionToValue, value],
    );

    const getOptionToLabel = useCallback(
        (option: Option) => {
            if (isSentinel(option) || option === null) {
                return '';
            }

            return optionToLabel(option);
        },
        [isSentinel, optionToLabel],
    );

    const isOptionEqualToValue = useCallback(
        (option: Option, value: Option) => {
            return isSentinel(option) ? false : optionToValue(option) === optionToValue(value);
        },
        [isSentinel, optionToValue],
    );

    const onChange = useCallback(
        (_event: SyntheticEvent, newValue: Option | NonNullable<Option> | null) => {
            switch (newValue) {
                case SHOW_MORE_SENTINEL:
                    return;
                case null:
                    if (disableClearable) {
                        return;
                    }
                    if (handleNullValue) {
                        handleNullValue();
                    } else {
                        reset();
                    }
                    break;
                default:
                    setInputState(optionToValue(newValue));
            }

            // If we are in `specialAutocompleteOnlyToAddMode`, then as soon as the user selects
            //  an option, we "clear" the text field, so they can select another.
            if (specialAutocompleteOnlyToAddMode) {
                setAutocompleteTypedText('');
            }
        },
        [
            disableClearable,
            handleNullValue,
            optionToValue,
            reset,
            setInputState,
            specialAutocompleteOnlyToAddMode,
        ],
    );

    // Pins the hint above the options. Memoized on the hint so the popup keeps a
    // stable component type between renders and the list is not remounted while
    // the user types.
    const HintPaper = useMemo(
        () =>
            hint
                ? ({ children, ...paperProps }: HTMLAttributes<HTMLElement>) => (
                      <Paper {...paperProps}>
                          <Typography
                              variant='caption'
                              color='text.secondary'
                              data-testid='field-hint'
                              sx={{ display: 'block', px: 2, pt: 1 }}
                          >
                              {hint}
                          </Typography>
                          {children}
                      </Paper>
                  )
                : undefined,
        [hint],
    );

    const filterOptionsProp = useMemo(
        () =>
            typeof filterOptions === 'function'
                ? (optionsList: Option[]) =>
                      // If the caller wants to filter options, then we have to
                      //  reimplement the fuzzy matching ourselves.
                      fuzzyFilterOptions({
                          options: filterOptions(optionsList),
                          typedText: autocompleteTypedText,
                          isSentinel,
                          optionToLabel,
                      })
                : undefined,
        [autocompleteTypedText, filterOptions, isSentinel, optionToLabel],
    );

    return (
        <FieldWithAxleLabel
            label={label}
            inlineLabel={inlineLabel}
            tooltipText={tooltipText}
            required={required}
            enabled={enabled}
            disabledHelperText={disabledHelperText}
            sx={style}
            description={description}
        >
            <FormControl variant='outlined' fullWidth>
                <Autocomplete
                    PopperComponent={expandMenuToFitContent ? FitContentPopper : undefined}
                    disabled={!enabled}
                    // These control the dropdown options of the filed.
                    options={optionsProp as unknown as Option[]}
                    getOptionLabel={getOptionToLabel}
                    isOptionEqualToValue={isOptionEqualToValue}
                    // These control the “actual value” of the field.
                    value={
                        matchingOptionValue ??
                        (value !== null && displayMissingOptionValue
                            ? // TODO(jessica): This cast represents a limitation around
                              // displaying missing selected values, namely, that we can
                              // only do that when the input value and options are of the
                              // same type. We should improve this in the future.
                              (value as unknown as Option)
                            : null)
                    }
                    onChange={onChange}
                    open={isDropdownOpen}
                    onOpen={openDropdown}
                    onClose={closeDropdown}
                    getOptionDisabled={
                        optionToDisabled
                            ? (option) => !isSentinel(option) && optionToDisabled(option)
                            : undefined
                    }
                    PaperComponent={HintPaper}
                    // These control what text is currently shown as typed in the field.
                    inputValue={autocompleteTypedText}
                    onInputChange={(event, newValue) => setAutocompleteTypedText(newValue)}
                    // Whether to allow clearing the input field (to an empty value)
                    disableClearable={
                        // If we are in `specialAutocompleteOnlyToAddMode`, then it doesn't make sense to "clear"
                        //  the input, since the input itself never has a selected value. Hence, if
                        //  `specialAutocompleteOnlyToAddMode` is true, we force `disableClearable` to be true.
                        specialAutocompleteOnlyToAddMode || disableClearable
                    }
                    // The input component.
                    renderInput={(props) => (
                        <TextField
                            {...props}
                            label={
                                inlineLabel
                                    ? labelWithRequiredAsterisk({ label, required })
                                    : undefined
                            }
                            variant={'outlined'}
                            placeholder={placeholder}
                        />
                    )}
                    size='small'
                    // The options component.
                    // We needed to override this, because the default implementation
                    //  uses the option label as the key, but in the dropdown to select
                    //  QHPs, sometimes different QHPs have the same name, and React complains.
                    //  The real solution is probably making the labels different, as if React
                    //  doesn't know the difference between the options, the user won't either.
                    // TODO: Delete this once we figure out how to distinguish the labels of QHPS
                    //  with the same name.
                    renderOption={(props, option) => {
                        // Render the "Show More" button instead of a regular option
                        if (option === SHOW_MORE_SENTINEL) {
                            return (
                                <Box
                                    key='show-more-button'
                                    sx={{
                                        p: 1.5,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        bgcolor: 'grey.50',
                                    }}
                                >
                                    <Button
                                        fullWidth
                                        size='small'
                                        variant='outlined'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setShowMore(true);
                                        }}
                                    >
                                        {showMoreButtonText} ({moreOptions.length} more)
                                    </Button>
                                </Box>
                            );
                        }
                        if (option === DEFAULT_OPTIONS_SENTINEL) {
                            return (
                                <Box
                                    key='default-options-label'
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        bgcolor: 'grey.50',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Default Options
                                </Box>
                            );
                        }
                        if (option === MORE_OPTIONS_SENTINEL) {
                            return (
                                <Box
                                    key='more-options-label'
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        bgcolor: 'grey.50',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    More Options
                                </Box>
                            );
                        }
                        const optionContent = renderOption
                            ? renderOption(option)
                            : optionToLabel(option);
                        const tag = optionToTag?.(option) ?? null;
                        return (
                            <Box
                                component='li'
                                {...props}
                                key={JSON.stringify(optionToValue(option))}
                                sx={optionStyle}
                            >
                                {tag !== null ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            width: '100%',
                                        }}
                                    >
                                        <Box>{optionContent}</Box>
                                        <Typography
                                            variant='caption'
                                            color='text.secondary'
                                            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                                        >
                                            {tag}
                                        </Typography>
                                    </Box>
                                ) : (
                                    optionContent
                                )}
                            </Box>
                        );
                    }}
                    noOptionsText={noOptionsText}
                    // Grouping fields.
                    groupBy={groupBy}
                    renderGroup={renderGroup}
                    // Filtering options workaround.
                    filterOptions={filterOptionsProp}
                    // Cosmetic enhancements.
                    autoComplete={true} // Makes the remainder of the autocompleted option show up in text field.
                    sx={{
                        '& .MuiAutocomplete-inputRoot': { ..._extraAutocompleteStyles },
                        '&& .MuiAutocomplete-inputRoot': { ..._extraAutocompleteStyles },
                    }}
                />
                <If condition={!hideHelperText}>
                    <FormHelperText error={showError}>{helperText}</FormHelperText>
                </If>
            </FormControl>
        </FieldWithAxleLabel>
    );
};
